import { useEffect, useRef } from "react"
import { supabase } from "@/lib/supabase/client"
import { isMockMode } from "@/lib/mock/mock-supabase"
import { useTenant } from "@/hooks/useTenant"

/**
 * Processa requests de recálculo de IVE disparados por automações
 * (task #15 v2, action `calculate_ive`).
 *
 * Como funciona:
 *   1. Automação SQL marca lead.ive_recalc_requested_at = now()
 *   2. Este hook escuta UPDATE em credit_v2_leads via Realtime
 *   3. Quando vê ive_recalc_requested_at recente (e tenant é do user),
 *      invoca a edge function run-ive-analysis com a sessão do user
 *   4. Edge function recalcula e atualiza ive_analisado_em do lead;
 *      este hook compara ive_analisado_em > ive_recalc_requested_at pra
 *      saber se já foi processado.
 *
 * Catch-up on mount: também busca leads com ive_recalc_requested_at >
 * ive_analisado_em (ou ive_analisado_em null) e processa.
 *
 * Por que essa arquitetura: armazenar service_role_key no DB pra a SQL
 * function invocar a edge function direto seria credential leak (vault
 * tem permission issues e GUC é queryable). Esta abordagem usa a sessão
 * do user logado pra autenticar — mesma pattern do botão "Recalcular IVE"
 * manual no LeadDrawer.
 *
 * Limitação: só processa enquanto há pelo menos 1 user ativo no tenant.
 * Pra automações 100% headless precisaria de worker dedicado.
 */
export function useAutomationIveRequestProcessor(): void {
  const { activeTenantId } = useTenant()
  // Trackeia leads já processados nessa sessão pra não invocar duplicado
  // se Realtime mandar o mesmo evento múltiplas vezes.
  const processedRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!activeTenantId || isMockMode) return

    const processLead = async (leadId: string) => {
      if (processedRef.current.has(leadId)) return
      processedRef.current.add(leadId)
      try {
        await supabase.functions.invoke("run-ive-analysis", {
          body: { lead_id: leadId, tenant_id: activeTenantId, triggered_by: "auto" },
        })
        // Limpa do set depois de 60s pra permitir reprocessamento se houver
        // segunda automação no mesmo lead
        setTimeout(() => processedRef.current.delete(leadId), 60_000)
      } catch (err) {
        console.warn("[useAutomationIveRequestProcessor] invoke failed:", err)
        processedRef.current.delete(leadId)  // permite retry
      }
    }

    // Catch-up: leads com recalc requested após análise anterior
    const fetchPending = async () => {
      // 5 minutos de janela — não pega coisa muito antiga
      const sinceISO = new Date(Date.now() - 5 * 60_000).toISOString()
      const { data } = await supabase
        .from("credit_v2_leads")
        .select("id, ive_recalc_requested_at, ive_analisado_em")
        .eq("tenant_id", activeTenantId)
        .gte("ive_recalc_requested_at", sinceISO)
      if (!data) return
      for (const lead of data) {
        const rec = lead.ive_recalc_requested_at
        const ana = lead.ive_analisado_em
        if (rec && (!ana || new Date(ana) < new Date(rec))) {
          void processLead(lead.id)
        }
      }
    }
    void fetchPending()

    // Realtime: escuta updates do tenant
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const channel = (supabase.channel(`automation_ive_recalc:${activeTenantId}`) as any)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "credit_v2_leads",
          filter: `tenant_id=eq.${activeTenantId}`,
        },
        (payload: { new: { id: string; ive_recalc_requested_at?: string; ive_analisado_em?: string } }) => {
          const newRow = payload.new
          if (!newRow.ive_recalc_requested_at) return
          // Só processa se a flag é mais recente que a análise
          if (newRow.ive_analisado_em && new Date(newRow.ive_analisado_em) >= new Date(newRow.ive_recalc_requested_at)) {
            return
          }
          void processLead(newRow.id)
        },
      )
      .subscribe()

    return () => {
      void supabase.removeChannel(channel)
    }
  }, [activeTenantId])
}
